var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import Config from './Config';
import SendRoutine from './SendRoutine';
import Device from './Device';
import Personalize from './Personalize';
import FormSubmit from './FormSubmit';
import ConsentManager from './Consent';
import { captureAdMatchIds } from './AdMatchIds';
import { capturePid } from './PidReveal';
import { isLikelyBot } from './BotDetect';
import { resolveCurrentProduct, resolveCurrentContent, resolvePageLang } from './product';
var Tracker = /** @class */ (function () {
    function Tracker(client, instance) {
        var _this = this;
        var _a;
        // Events tracked while the analytics purpose is not granted. Kept in memory
        // only (never in the localStorage queue, which would store the device id on
        // the visitor's browser without consent) and moved to the real queue if
        // consent arrives on this same page; lost on navigation otherwise.
        this.pending = [];
        this.topicsInFlight = false;
        this.client = client;
        this.instance = instance;
        this.isBot = isLikelyBot(navigator.userAgent);
        // Device before SendRoutine: the sync callback below closes over
        // `this.device`, and needs it to exist as soon as the first /events
        // response can come back.
        this.device = new Device(function (oldId, newId) { return _this.trackSwitchId(oldId, newId); }, Tracker.storedAnalyticsGranted());
        this.sendroutine = new SendRoutine(false, function (did) { return _this.device.syncDeviceID(did); });
        // Constructed before Personalize: buildNode() wires each shadow root it
        // creates onto this instance (submit doesn't cross a shadow boundary, so
        // FormSubmit's own document-level listener can't reach a
        // shadow-encapsulated form on its own — see FormSubmit.ts).
        this.formSubmit = new FormSubmit(function (formId, fields) {
            var _a;
            _this.trackFormSubmit(formId, fields);
            // Mirror the CTA-click flush: a form's own post-submit redirect
            // shouldn't strand the event in the queue. Queue path only — without
            // analytics consent trackFormSubmit already sent directly, and flushing
            // here would leak consent-gated events waiting in the queue.
            if (_this.analyticsGranted())
                (_a = _this.sendroutine) === null || _a === void 0 ? void 0 : _a.flush();
        });
        this.personalize = new Personalize(this.client, this.instance, 
        // The device id reaches /personalize/serve only with the analytics
        // purpose granted; without it the call is anonymous (no X-Device-Id
        // header, no cookies) and gets the generic content.
        { getDeviceID: function () { return (_this.analyticsGranted() ? _this.device.getDeviceID() : undefined); } }, 
        // Let the personalizer emit its impression / CTA-click events through the
        // same queue + consent path as every other event. CTA clicks flush
        // immediately so a navigation away (esp. cross-origin) doesn't strand them.
        function (eventType, eventData) {
            var _a;
            // Rendering a component is consent-free, but its performance events
            // (impressions / CTA clicks) are analytics: without the analytics
            // purpose they are dropped outright — queueing them would grow
            // localStorage unboundedly while the send routine is stopped.
            if (!_this.analyticsGranted())
                return;
            _this.sendEvent(eventType, eventData);
            if (eventType === "click")
                (_a = _this.sendroutine) === null || _a === void 0 ? void 0 : _a.flush();
        }, this.formSubmit);
        // The ConsentManager (localStorage-backed) is the SINGLE source of truth
        // for whether analytics may run. Its onChange callback re-evaluates the
        // send gate whenever consent changes, so the event's `consent` field and
        // the decision to send can never disagree (core-input drops events whose
        // consent denies analytics). Its onSync callback emits a `consent` event
        // on the ordinary tracking pipeline whenever the state actually changes
        // (or the resync interval elapses) — see trackConsent.
        this.consent = new ConsentManager(function () { return _this.applyConsentGate(); }, function (state) { return _this.trackConsent(state); });
        // A site may still drive consent through the legacy window flag
        // (window.cdpfGdprConsent.status = "granted"|"denied"); bridge it into the
        // ConsentManager so both mechanisms agree. installGdprBridge also honours a
        // flag already set before the tracker loaded.
        this.installGdprBridge();
        // Apply whatever consent is already known (persisted locally or bridged
        // from the window flag above).
        this.applyConsentGate();
        // Personalization and Form Builder interception are NOT consent-gated:
        // rendering a component is content delivery, not profiling, and a Form
        // Builder form carries its own mandatory data-processing acceptance (its
        // events bypass the analytics gate — see trackFormSubmit). Only the
        // analytics flow (event queue/send, topics, ad ids) stays behind consent.
        //
        // Started a microtask later, not right away: the bundle's bootstrap
        // (main.ts) replays consent queued by the install snippet just after this
        // constructor returns, and the first /personalize/serve call must already
        // see that consent to carry the device id.
        void Promise.resolve().then(function () { var _a; return (_a = _this.personalize) === null || _a === void 0 ? void 0 : _a.start(); });
        (_a = this.formSubmit) === null || _a === void 0 ? void 0 : _a.start();
    }
    /** True when the visitor has granted the analytics purpose. */
    Tracker.prototype.analyticsGranted = function () {
        var _a;
        return ((_a = this.consent.getConsent()) === null || _a === void 0 ? void 0 : _a.analytics) === true;
    };
    /**
     * Same check as analyticsGranted(), usable before the ConsentManager exists
     * (Device is built first, and must not write its ids if consent is absent).
     * A legacy `window.cdpfGdprConsent.status = "granted"` set before the
     * tracker loaded is bridged later and persists the ids then.
     */
    Tracker.storedAnalyticsGranted = function () {
        var _a;
        try {
            var raw = localStorage.getItem(Config.LS_CONSENT_KEY);
            return raw ? ((_a = JSON.parse(raw)) === null || _a === void 0 ? void 0 : _a.analytics) === true : false;
        }
        catch (_b) {
            return false;
        }
    };
    /**
     * Start every consent-gated subsystem. Each start() is idempotent.
     * Personalize / FormSubmit are not here: they run consent-free (started in
     * the constructor) — only their analytics events remain gated.
     */
    Tracker.prototype.startPipeline = function () {
        var _this = this;
        var _a;
        (_a = this.sendroutine) === null || _a === void 0 ? void 0 : _a.start();
        this.trackTopics();
        captureAdMatchIds(function (idType, value) { return _this.trackExternalId(idType, value); });
        capturePid(function (encrypted) { return _this.sendEvent("externalid", { externalIdEncrypted: encrypted }); });
    };
    /** (Re)evaluate the analytics gate: start when granted, stop when not. */
    Tracker.prototype.applyConsentGate = function () {
        var _a, _b;
        var granted = this.analyticsGranted();
        // Ids are stored only with consent, bots included: a bot never sends, but
        // the storage rule doesn't depend on who is visiting.
        this.device.setPersistent(granted);
        if (granted) {
            // Events tracked on this page before consent arrived go out now, stamped
            // with the consent that allows them.
            var consent = (_a = this.consent.getConsent()) !== null && _a !== void 0 ? _a : undefined;
            for (var _i = 0, _c = this.pending; _i < _c.length; _i++) {
                var event = _c[_i];
                this.enqueueEvent(__assign(__assign({}, event), { consent: consent }));
            }
            this.pending = [];
            if (!this.isBot)
                this.startPipeline();
        }
        else {
            (_b = this.sendroutine) === null || _b === void 0 ? void 0 : _b.stop();
            this.pending = [];
            // Anything still queued carries the device id: without consent it must
            // neither be sent nor stay in the browser.
            try {
                localStorage.removeItem(Config.LS_EVENTS_QUEUE);
            }
            catch (_d) {
                // ignore
            }
        }
    };
    /**
     * Bridge the legacy `window.cdpfGdprConsent` flag into the ConsentManager,
     * so the manager remains the single source of truth. Reads any value already
     * present and installs a Proxy to translate later changes.
     */
    Tracker.prototype.installGdprBridge = function () {
        var _this = this;
        var existing = window[Config.GDPR_CONSENT_VAR];
        if (existing === null || existing === void 0 ? void 0 : existing.consentString)
            this.consentString = existing.consentString;
        if (existing === null || existing === void 0 ? void 0 : existing.status)
            this.bridgeStatus(existing.status);
        window[Config.GDPR_CONSENT_VAR] = new Proxy(existing || {}, {
            set: function (target, prop, value) {
                if (prop === 'status')
                    _this.bridgeStatus(value);
                if (prop === 'consentString')
                    _this.consentString = value;
                target[prop] = value;
                return true;
            },
        });
    };
    /**
     * Translate the coarse window flag into the analytics purpose the gate
     * uses. setConsent() itself owns the dedup/resync decision (see
     * ConsentManager), so this always delegates to it rather than
     * short-circuiting when the value already matches.
     */
    Tracker.prototype.bridgeStatus = function (status) {
        this.consent.setConsent({ analytics: status === 'granted' });
    };
    /**
     * Emit the current consent state as a `consent` event on the ordinary
     * tracking pipeline (ConsentManager's onSync callback — fired on a real
     * change or the periodic resync, never on every identical setConsent
     * call). Sent immediately via sendNow, bypassing the analytics-gated
     * queue: a consent record — including a denial — must reach the server
     * regardless of the very consent it represents, same reasoning as
     * trackFormSubmit's ungated exempt path.
     */
    Tracker.prototype.trackConsent = function (state) {
        var _a;
        if (this.isBot)
            return;
        void ((_a = this.sendroutine) === null || _a === void 0 ? void 0 : _a.sendNow([this.createEvent("consent")], this.analyticsGranted()));
    };
    Tracker.prototype.enqueueEvent = function (event) {
        var queue = JSON.parse(localStorage.getItem(Config.LS_EVENTS_QUEUE) || "[]");
        queue.push(event);
        localStorage.setItem(Config.LS_EVENTS_QUEUE, JSON.stringify(queue));
    };
    Tracker.prototype.sendEvent = function (eventType, eventData) {
        if (eventData === void 0) { eventData = {}; }
        if (this.isBot)
            return;
        var event = this.createEvent(eventType, eventData);
        if (this.analyticsGranted()) {
            this.enqueueEvent(event);
        }
        else if (this.pending.length < Tracker.MAX_PENDING_EVENTS) {
            this.pending.push(event);
        }
    };
    Tracker.prototype.send = function (action) {
        var params = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            params[_i - 1] = arguments[_i];
        }
        switch (action) {
            case "view":
                this.trackPageView(params[0]);
                break;
            case "click":
                this.trackClick(params[0]);
                break;
            case "purchase":
                this.trackPurchase(params[0], params[1]);
                break;
            case "cartadd":
                this.trackCartAdd(params[0]);
                break;
            case "cartremove":
                this.trackCartRemove(params[0]);
                break;
            case "externalid":
                this.trackExternalId(params[0], params[1], params[2]);
                break;
            case "user":
                this.trackUser(params[0], params[1], params[2]);
                break;
            case "formsubmit":
                this.trackFormSubmit(params[0], params[1]);
                break;
        }
    };
    Tracker.prototype.trackTopics = function () {
        var _a, _b;
        return __awaiter(this, void 0, Promise, function () {
            var settings;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        if (this.topicsInFlight || sessionStorage.getItem(Config.LS_TOPICS_SENT_FLAG)) {
                            return [2 /*return*/];
                        }
                        this.topicsInFlight = true;
                        _c.label = 1;
                    case 1:
                        _c.trys.push([1, , 4, 5]);
                        return [4 /*yield*/, ((_a = this.personalize) === null || _a === void 0 ? void 0 : _a.trackingSettings())];
                    case 2:
                        settings = _c.sent();
                        if (!(settings === null || settings === void 0 ? void 0 : settings.browsingTopics) || !this.analyticsGranted())
                            return [2 /*return*/];
                        return [4 /*yield*/, ((_b = this.sendroutine) === null || _b === void 0 ? void 0 : _b.sendTopics(this.createEvent("topics")))];
                    case 3:
                        _c.sent();
                        sessionStorage.setItem(Config.LS_TOPICS_SENT_FLAG, Date.now().toString());
                        return [3 /*break*/, 5];
                    case 4:
                        this.topicsInFlight = false;
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Bind the visitor to a known identity (e.g. email) at login. Optionally
     * carries anagraphic `traits` (name, phone, ...) so the pipeline can populate
     * the person's profile without a batch import. Traits are attached at most
     * once a week per identical payload (client-side guard); the identity binding
     * itself is always sent.
     */
    Tracker.prototype.trackExternalId = function (externalIdType, externalId, traits) {
        var data = { externalIdType: externalIdType, externalId: externalId };
        if (traits && Object.keys(traits).length && this.shouldSendTraits(traits)) {
            data.traits = traits;
        }
        this.sendEvent("externalid", data);
    };
    /**
     * Convenience alias for sending anagraphic info for a logged-in user: emits
     * an `externalid` event carrying the identity and the traits. Same weekly
     * client-side guard as {@link trackExternalId}.
     */
    Tracker.prototype.trackUser = function (traits, externalIdType, externalId) {
        if (externalIdType === void 0) { externalIdType = "email"; }
        var id = externalId !== null && externalId !== void 0 ? externalId : traits.email;
        if (!id) {
            console.warn("trackUser requires an externalId or traits.email, skipped !");
            return;
        }
        this.trackExternalId(externalIdType, id, traits);
    };
    /**
     * Client half of the "once a week" double guard: returns false when an
     * identical traits payload was already sent within USER_TRAITS_TTL. A stable
     * hash of the sorted payload is stored in localStorage with the send time.
     */
    Tracker.prototype.shouldSendTraits = function (traits) {
        try {
            var hash = stableHash(traits);
            var key = "".concat(Config.LS_USER_TRAITS_CACHE, ":").concat(hash);
            var last = Number(localStorage.getItem(key) || 0);
            if (Date.now() - last < Config.USER_TRAITS_TTL)
                return false;
            localStorage.setItem(key, Date.now().toString());
            return true;
        }
        catch (_a) {
            // localStorage unavailable (private mode / quota): fail open and send.
            return true;
        }
    };
    /**
     * A Form Builder form was submitted (FormSubmit intercepts the native
     * submit and calls this). Always logs a `form_submit` event carrying every
     * raw field value (in `formFields`) plus `traits` (every field but
     * `email`) for the merge-safe profile upsert already wired to any event
     * carrying traits — no pipeline change needed. The `email` field, when
     * present, is the reserved key that feeds the identity graph: it is sent
     * again as a plain {@link trackExternalId} identify call, the same path a
     * login uses, so the person is resolved/merged exactly as it would be for
     * a known login. No traits on that second event — `form_submit` already
     * carries them ungated, so it doesn't need (and shouldn't fight with) the
     * weekly resend guard trackExternalId applies to traits.
     *
     * NOT consent-gated: submitting a Form Builder form implies the visitor's
     * explicit acceptance of data processing (the mandatory privacy checkbox on
     * the form itself), so its events must reach the server even when the
     * analytics purpose was denied or never answered. With analytics granted
     * they travel on the ordinary queue; without it they are sent immediately,
     * bypassing the queue so no other consent-gated event can ride along. The
     * identify event is stamped with the formId so core-input can recognise it
     * as part of the exempt submission (see core-input's consent gate).
     */
    Tracker.prototype.trackFormSubmit = function (formId, fields) {
        var _a;
        if (this.isBot)
            return;
        if (!formId || !fields || Object.keys(fields).length === 0) {
            console.warn("trackFormSubmit requires a formId and at least one field, skipped !");
            return;
        }
        var email = fields.email, traits = __rest(fields, ["email"]);
        var events = [
            this.createEvent("form_submit", { formId: formId, formFields: fields, traits: traits }),
        ];
        var externalId = email != null ? String(email).trim() : "";
        if (externalId !== "") {
            events.push(this.createEvent("externalid", { externalIdType: "email", externalId: externalId, formId: formId }));
        }
        if (this.analyticsGranted()) {
            for (var _i = 0, events_1 = events; _i < events_1.length; _i++) {
                var event = events_1[_i];
                this.enqueueEvent(event);
            }
        }
        else {
            void ((_a = this.sendroutine) === null || _a === void 0 ? void 0 : _a.sendNow(events, false));
        }
    };
    Tracker.prototype.trackSwitchId = function (oldId, newId) {
        this.sendEvent("switchid", { oldId: oldId, newId: newId });
    };
    /**
     * Track a pageview. On a product (or article) detail page the current item is
     * attached to the event and becomes a recommender "view" feedback. Pass the
     * product explicitly, or let it be resolved fresh from the page's meta/Open
     * Graph tags: a product page → `products[]`, an article page (og:type=article
     * / cdpf:content) → `content[]`. On other pages nothing is attached.
     */
    Tracker.prototype.trackPageView = function (product) {
        var resolvedProduct = product !== null && product !== void 0 ? product : resolveCurrentProduct();
        if (resolvedProduct) {
            this.sendEvent("view", { products: [resolvedProduct] });
            return;
        }
        var content = resolveCurrentContent();
        this.sendEvent("view", content ? { content: [content] } : {});
    };
    Tracker.prototype.trackClick = function (target) {
        if (target == null) {
            console.warn("Target is null, click event skipped !");
            return;
        }
        this.sendEvent("click", { target: target });
    };
    Tracker.prototype.trackPurchase = function (orderId, products) {
        var _a;
        var productsArray = this.validateProducts(products, "purchase");
        if (!productsArray)
            return;
        // Last-touch attribution: enrich the purchase event with the variant (and
        // backing recommendation) of the most recent experiment the visitor was
        // exposed to, so the conversion is measured without a separate event.
        var data = { order: orderId, products: productsArray };
        var exposure = (_a = this.personalize) === null || _a === void 0 ? void 0 : _a.lastAssignment();
        if (exposure) {
            data.personalizationId = exposure.personalizationId;
            data.variantId = exposure.variantId;
            if (exposure.recommendationId != null) {
                data.recommendationId = exposure.recommendationId;
            }
        }
        this.sendEvent("purchase", data);
    };
    /**
     * A product was added to the cart. Same product validation as
     * {@link trackPurchase}, no order id: `quantity` carries how many units were
     * added.
     */
    Tracker.prototype.trackCartAdd = function (products) {
        var productsArray = this.validateProducts(products, "cartadd");
        if (!productsArray)
            return;
        this.sendEvent("cartadd", { products: productsArray });
    };
    /**
     * A product was removed from the cart (or its quantity decreased).
     * `quantity` carries how many units were removed.
     */
    Tracker.prototype.trackCartRemove = function (products) {
        var productsArray = this.validateProducts(products, "cartremove");
        if (!productsArray)
            return;
        this.sendEvent("cartremove", { products: productsArray });
    };
    /**
     * Shared product-payload validation for purchase/cartadd/cartremove: every
     * line item needs id/price/quantity, currency defaults to EUR. Returns
     * undefined (and warns) when any product is missing a required field.
     */
    Tracker.prototype.validateProducts = function (products, eventType) {
        var productsArray = Array.isArray(products) ? products : [products];
        var hasInvalidProducts = productsArray.some(function (product) {
            if (product.id == null || product.price == null || product.quantity == null) {
                console.warn("Product is missing required fields, ".concat(eventType, " event skipped !"));
                return true;
            }
            product.currency = product.currency || "EUR";
            return false;
        });
        return hasInvalidProducts ? undefined : productsArray;
    };
    Tracker.prototype.createEvent = function (eventType, eventData) {
        var _a, _b, _c, _d;
        if (eventData === void 0) { eventData = {}; }
        var consentState = this.consent.getConsent();
        return __assign({ client: this.client, instance: this.instance, event: eventType, href: window.location.href, pageTitle: ((_a = document.querySelector('meta[property="og:title"]')) === null || _a === void 0 ? void 0 : _a.content) || document.title, pageDescription: (_b = document.querySelector('meta[property="og:description"]')) === null || _b === void 0 ? void 0 : _b.content, pageImage: (_c = document.querySelector('meta[property="og:image"]')) === null || _c === void 0 ? void 0 : _c.content, pageType: (_d = document.querySelector('meta[property="og:type"]')) === null || _d === void 0 ? void 0 : _d.content, lang: resolvePageLang(), referrer: document.referrer, timestamp: new Date().toISOString(), did: this.device.getDeviceID(), session: this.device.getSession(), gdpr: this.consentString, consent: consentState !== null && consentState !== void 0 ? consentState : undefined }, eventData);
    };
    // Cap on the in-memory pre-consent buffer, so a long-lived SPA page whose
    // visitor never answers the banner can't grow it without bound.
    Tracker.MAX_PENDING_EVENTS = 100;
    return Tracker;
}());
export default Tracker;
/**
 * Order-independent string hash of a flat traits object, so `{a,b}` and `{b,a}`
 * dedupe to the same key. Small djb2 over the sorted `key=value` pairs.
 */
function stableHash(obj) {
    var serialized = Object.keys(obj)
        .sort()
        .map(function (k) { return "".concat(k, "=").concat(String(obj[k])); })
        .join('&');
    var h = 5381;
    for (var i = 0; i < serialized.length; i++) {
        h = (h * 33) ^ serialized.charCodeAt(i);
    }
    return (h >>> 0).toString(36);
}
